import { DirectiveBinding } from 'vue'

import { usePermissionStore } from './permission-store'

/**
 * This file provides two custom Vue directives for permission-based UI control:
 *
 * 1. v-permission: Shows or hides an element based on user permissions.
 *    - Example: <button v-permission="'Resource.Code'">Edit</button>
 *
 * 2. v-permission-disabled: Enables or disables an element based on user permissions.
 *    - Example: <button v-permission-disabled="['A', 'B']">Action</button>
 */

type PermissionValue = string | string[]

// Check permissions: any or all
const createPermissionChecker = (store: ReturnType<typeof usePermissionStore>, codes: string[], isAny: boolean) =>
  isAny ? store.hasAnyPermission(codes) : store.hasAllPermissions(codes)

// Factory for permission directives
const createPermissionDirective = (onAllow: (el: HTMLElement) => void, onDeny: (el: HTMLElement) => void) => ({
  // On mount: check permission and update element
  async mounted(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
    const store = usePermissionStore()
    const codes = Array.isArray(binding.value) ? binding.value : [binding.value]
    if (!codes.length) return
    try {
      const hasPermission = await createPermissionChecker(store, codes, binding.modifiers.any!)
      if (hasPermission) {
        onAllow(el)
      } else {
        onDeny(el)
      }
    } catch {
      onDeny(el)
    }
  },
  // On update: re-check permission
  async updated(el: HTMLElement, binding: DirectiveBinding<PermissionValue>) {
    const store = usePermissionStore()
    const codes = Array.isArray(binding.value) ? binding.value : [binding.value]
    if (!codes.length) return
    try {
      const hasPermission = await createPermissionChecker(store, codes, binding.modifiers.any!)
      if (hasPermission) {
        onAllow(el)
      } else {
        onDeny(el)
      }
    } catch {
      onDeny(el)
    }
  },
})

// v-permission: show/hide element
export const vPermission = createPermissionDirective(
  (el) => {
    el.style.display = ''
    el.removeAttribute('data-permission-hidden')
  },
  (el) => {
    el.style.display = 'none'
    el.setAttribute('data-permission-hidden', 'true')
  },
)

// v-permission-disabled: enable/disable element
export const vPermissionDisabled = createPermissionDirective(
  (el) => {
    ;(el as any).disabled = false
    el.removeAttribute('data-permission-disabled')
  },
  (el) => {
    ;(el as any).disabled = true
    el.setAttribute('data-permission-disabled', 'true')
  },
)
